import { ReactNode } from 'react'
import dayjs from 'dayjs'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import {
  AdministrationStatus,
  MarChartData,
  MedicationTableData,
  MedicationTableRow
} from '../types'
import Administered from '../iconsV2/Administered'
import Discontinued from '../iconsV2/Discontinued'
import Missed from '../iconsV2/Missed'
import Paused from '../iconsV2/Paused'
import NotScheduledOrNotNeeded from '../iconsV2/NotScheduledOrNotNeeded'

const StyledCode = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.color.neutral.color3};
`

export type AdministrationStatusDisplay = {
  display: ReactNode
  translationKey: string
}

export const getAdministrationStatusDisplay = (
  status: AdministrationStatus
): AdministrationStatusDisplay => {
  switch (status) {
    case AdministrationStatus.GIVEN:
      return {
        display: <Administered />,
        translationKey: 'ADMINISTERED'
      }
    case AdministrationStatus.VERBAL_PROMPT:
      return {
        display: <StyledCode level={2}>VP</StyledCode>,
        translationKey: 'VERBAL_PROMPT'
      }
    case AdministrationStatus.ABSENT:
      return {
        display: <StyledCode level={2}>A</StyledCode>,
        translationKey: 'ABSENT'
      }
    case AdministrationStatus.OTHER:
      return {
        display: <StyledCode level={2}>O</StyledCode>,
        translationKey: 'OTHER'
      }
    case AdministrationStatus.ALREADY_TAKEN:
      return {
        display: <StyledCode level={2}>AT</StyledCode>,
        translationKey: 'ALREADY_TAKEN'
      }
    case AdministrationStatus.NOT_NEEDED:
      return {
        display: <NotScheduledOrNotNeeded />,
        translationKey: 'NOT_SCHEDULED_OR_NOT_NEEDED'
      }
    case AdministrationStatus.LEFT_OUT:
      return {
        display: <StyledCode level={2}>LO</StyledCode>,
        translationKey: 'LEFT_OUT'
      }
    case AdministrationStatus.PAUSED:
      return {
        display: <Paused />,
        translationKey: 'PAUSED'
      }
    case AdministrationStatus.NAUSEA_VOMITING:
      return {
        display: <StyledCode level={2}>N/V</StyledCode>,
        translationKey: 'NAUSEA_OR_VOMITING'
      }
    case AdministrationStatus.DISCONTINUED:
      return {
        display: <Discontinued />,
        translationKey: 'DISCONTINUED'
      }
    case AdministrationStatus.REFUSED:
      return {
        display: <StyledCode level={2}>R</StyledCode>,
        translationKey: 'REFUSED'
      }
    case AdministrationStatus.MISSED:
      return {
        display: <Missed />,
        translationKey: 'MISSED'
      }
    case AdministrationStatus.REFUSED_DESTROYED:
      return {
        display: <StyledCode level={2}>RD</StyledCode>,
        translationKey: 'REFUSED_AND_DESTROYED'
      }
    default:
      return {
        display: <Administered />,
        translationKey: 'ADMINISTERED'
      }
  }
}

export const getAdministrationSubtitle = (
  status: AdministrationStatus | undefined,
  doseQuantity: number,
  unit: string,
  t: (key: string) => string
): string | undefined => {
  if (!status) {
    return undefined
  }

  if (doseQuantity === 0) {
    return t('NO_CHANGE_TO_STOCK')
  }

  return `${doseQuantity} ${unit ? unit : ''}`
}

export const getAllAdministrationStatuses = (): AdministrationStatus[] => {
  const excludedStatuses = [
    AdministrationStatus.SCHEDULED,
    AdministrationStatus.OVERDUE,
    AdministrationStatus.PRN_AVAILABLE,
    AdministrationStatus.UNKNOWN,
    AdministrationStatus.NOT_NEEDED,
    AdministrationStatus.MISSED,
    AdministrationStatus.PAUSED,
    AdministrationStatus.DISCONTINUED
  ]

  return Object.values(AdministrationStatus).filter(
    (status) => !excludedStatuses.includes(status)
  ) as AdministrationStatus[]
}

export const getAdministrationStatusOptions = (
  t: (key: string) => string
): Array<{ value: AdministrationStatus; label: string }> => {
  return getAllAdministrationStatuses().map((status) => {
    const display = getAdministrationStatusDisplay(status)
    return {
      value: status,
      label: t(display.translationKey)
    }
  })
}

const formatDose = (dose: string | object): string => {
  if (typeof dose === 'string') {
    return dose
  }
  if (typeof dose === 'object' && dose !== null) {
    return JSON.stringify(dose)
  }
  return ''
}

export const transformMarChartData = (
  data: MarChartData | null | undefined,
  type: 'scheduled' | 'prn',
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): MedicationTableRow[] => {
  if (!data || !data.medications) {
    return []
  }

  const rows: MedicationTableRow[] = []
  let rowIndex = 0

  const filteredMedications = data.medications.filter(
    (med) => med.prn === (type === 'prn')
  )

  filteredMedications.forEach((medication) => {
    medication.roundTimings.forEach((timing) => {
      const currentStock = medication.currentStock || 0
      const stockAlertLevel = (medication as any).stockAlertLevel

      const medicationData: MedicationTableData = {
        name: medication.medicationName,
        currentStock,
        startDate: dayjs(medication.startDate).format('DD-MM-YYYY'),
        endDate: medication.endDate
          ? dayjs(medication.endDate).format('DD-MM-YYYY')
          : '',
        isControlledDrug: medication.controlledDrug,
        lowStock:
          stockAlertLevel !== undefined &&
          currentStock > 0 &&
          currentStock <= stockAlertLevel,
        outOfStock: currentStock === 0,
        stockUnit: medication.stockUnit || medication.quantityType || '',
        stockAlertLevel
      }

      const row: MedicationTableRow = {
        key: `med-${medication.residentMedicationId}-${timing.roundTimingId}-${rowIndex}`,
        medication: medicationData,
        dose: formatDose(timing.dose),
        residentMedicationId: medication.residentMedicationId
      }

      if (type === 'scheduled') {
        row.hour =
          timing.timingTime ||
          timing.timingName ||
          timing.dateStatuses?.find((ds) => ds.timingTime || ds.timingName)
            ?.timingTime ||
          ''
      }

      const dateMap: Record<string, any> = {}

      timing.dateStatuses.forEach((dateStatus) => {
        const dateKey = dayjs(dateStatus.date).format('DD-MM-YYYY')
        const administrationStatus = dateStatus.status as AdministrationStatus

        if (!administrationStatus) {
          return
        }

        if (administrationStatus === AdministrationStatus.SCHEDULED) {
          if (!dateMap[dateKey]) {
            dateMap[dateKey] = {
              outcomes: []
            }
          }
          dateMap[dateKey].outcomes.push({
            action: 'SCHEDULED',
            date: dateKey
          })
          return
        }

        const statusDisplay =
          getAdministrationStatusDisplay(administrationStatus)

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            outcomes: []
          }
        }

        const outcome: any = {
          action: statusDisplay.translationKey,
          date: dateKey
        }

        if (
          administrationStatus === AdministrationStatus.GIVEN &&
          dateStatus.recordedBy
        ) {
          outcome.recordDetails = {
            quantity:
              dateStatus.doseQuantity?.toString() || formatDose(timing.dose),
            timeRecorded:
              dateStatus.administeredTime ||
              dayjs(dateStatus.date).toISOString(),
            recordedById: dateStatus.recordedBy.id,
            recordedBy: `${dateStatus.recordedBy.name.first} ${dateStatus.recordedBy.name.last}`,
            recordedByPicture: dateStatus.recordedBy.picture,
            comment: dateStatus.details,
            comments: dateStatus.details ? [dateStatus.details] : []
          }
        }

        if (dateStatus.administration) {
          outcome.administration = dateStatus.administration
          outcome.dateStatus = dateStatus
        }

        dateMap[dateKey].outcomes.push(outcome)
      })

      Object.keys(dateMap).forEach((dateKey) => {
        row[dateKey] = dateMap[dateKey]
      })

      rows.push(row)
      rowIndex++
    })
  })

  return rows
}
